import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DataTable from "react-data-table-component";
import API_ENDPOINTS from "../../api/apiEndpoints";
import apiManager from "../../api/apiManager";
import ServiceOrderStatus from "../../shared/Enums/ServiceOrderStatus";
import RepairOrderStatuses from "./RepairOrderStatuses";
import {
  showLoader,
  hideLoader,
} from "../../shared/Services/Loader/loaderService";
import { showErrorMessage, showWarningMessage } from "../../shared/Services/toasterService";
import { useSelectedDealership } from "../../hooks/useLocalStorage";
import { useLocalization } from "../../context/LocalizationContext";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import getUserRoleFromJWT from "../../shared/helpers/getUserRoleFromJWT";
import ServiceAction from "../../shared/Enums/ServiceAction";
import { UserRole } from "../../shared/Enums/UserRole";
import DeclinedRepairOrdersModal from "./DeclinedRepairOrdersModal";
import { generateDeclinedServicesReport } from "../../shared/helpers/excelGenerator";

const ReportOrderListing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routerLocation = useLocation();
  const [isStatusLocked, setIsStatusLocked] = useState(false);
  const { t: localization } = useLocalization();
  const [filteredData, setFilteredData] = useState([]);
  const [showDeclinedModal, setShowDeclinedModal] = useState(false);
  const [declinedItems, setDeclinedItems] = useState([]);
  const [currentRowData, setCurrentRowData] = useState(null);

  const statusDisplayMap = {
    [ServiceOrderStatus.NEW]: RepairOrderStatuses.NEW,
    [ServiceOrderStatus.SIGNED]: RepairOrderStatuses.SIGNED,
    [ServiceOrderStatus.IN_PROGRESS]: RepairOrderStatuses.IN_PROGRESS,
    [ServiceOrderStatus.COMPLETED]: RepairOrderStatuses.COMPLETED,
    [ServiceOrderStatus.PENDING_APPROVAL]: RepairOrderStatuses.PENDING_APPROVAL,
    [ServiceOrderStatus.APPROVAL_EXPIRED]: RepairOrderStatuses.APPROVAL_EXPIRED,
    [ServiceOrderStatus.DECLINED]: RepairOrderStatuses.DECLINED,
  };

  const allowedStatuses = Object.entries(statusDisplayMap).map(
    ([value, label]) => ({ value, label }),
  );
  const [selectedDealershipId] = useSelectedDealership();
  const [filters, setFilters] = useState({
    customerName: "",
    fromDate: null,
    toDate: null,
    status: location.state?.statusFilter || "",
    vin: "",
  });
  const [errors, setErrors] = useState({
    customerName: "",
    vin: "",
  });
  const applyFilters = async () => {
    if (!validateInputs()) return;
    try {
      showLoader();
      let fromDateFormatted = null;
      if (filters.fromDate) {
        const fromDate = new Date(filters.fromDate);
        fromDate.setHours(0, 0, 0, 0);
        fromDateFormatted = fromDate.toISOString();
      }

      let toDateFormatted = null;
      if (filters.toDate) {
        const toDate = new Date(filters.toDate);
        toDate.setHours(23, 59, 59, 999);
        toDateFormatted = toDate.toISOString();
      }
      const payload = {
        dealershipId: selectedDealershipId,
        statusCode: filters.status || null,
        customerName: filters.customerName || null,
        vin: filters.vin || null,
        startDate: fromDateFormatted,
        endDate: toDateFormatted,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      const response = await apiManager.post(
        API_ENDPOINTS.REPAIR_ORDER_GET_ALL,
        payload,
      );
      const orders = response.data;
      const mappedOrders = orders.map((item, index) => ({
        ...item,
        id: index + 1,
      }));
      setFilteredData(mappedOrders);
    } catch (error) {
      showErrorMessage(error.message || "Error applying filters");
    } finally {
      hideLoader();
    }
  };

  const clearFilters = async () => {
    setFilters((prev) => ({
      customerName: "",
      fromDate: null,
      toDate: null,
      vin: "",
      status: isStatusLocked ? prev.status : "",
    }));
    await fetchOrders();
  };

  const fetchRepairOrderById = async (repairOrderId) => {
    try {
      showLoader();
      const payload = { repairOrderId: repairOrderId };
      const response = await apiManager.post(
        API_ENDPOINTS.REPAIR_ORDER_ITEM,
        payload,
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    } finally {
      hideLoader();
    }
  };

  const fetchDeclinedItems = async (repairOrderId, rowData) => {
    try {
      showLoader();
      const payload = { repairOrderId: repairOrderId };
      const response = await apiManager.post(
        API_ENDPOINTS.REPAIR_ORDER_ITEM,
        payload,
      );
      if (response.status === 200 && response.data) {
        // Filter for declined items (status = 3)
        const declined = response.data.filter((item) => item.status === 3);
        setDeclinedItems(declined);
        setCurrentRowData(rowData);
        setShowDeclinedModal(true);
      } else {
        setDeclinedItems([]);
        setCurrentRowData(rowData);
        setShowDeclinedModal(true);
      }
    } catch (error) {
      showWarningMessage(
        error.message || localization("error_fetch_declined_items")
      );
    } finally {
      hideLoader();
    }
  };

  const handleDownloadDeclinedServicesReport = async () => {
    if (filteredData.length === 0) {
      showErrorMessage(localization("no_data_to_export"));
      return;
    }

    try {
      showLoader();
      
      // Collect all repair order IDs
      const repairOrderIds = filteredData.map(order => order.repairOrderId);
      
      // Send single API call with all IDs
      const payload = { repairOrderIds: repairOrderIds };
      const response = await apiManager.post(
        API_ENDPOINTS.REPAIR_ORDER_ITEMS_BULK,
        payload,
      );

      if (response.status === 200 && response.data && Array.isArray(response.data)) {
        // Filter for declined items (status = 3) and ensure they have order context
        const allDeclinedItems = response.data.filter(item => item.status === 3);

        if (allDeclinedItems.length === 0) {
          showErrorMessage(localization("no_data_to_export"));
          hideLoader();
          return;
        }

        generateDeclinedServicesReport(allDeclinedItems, filters);
      } else {
        showErrorMessage(localization("no_data_to_export"));
      }
    } catch (error) {
      showErrorMessage(error.message || localization("error_fetch_declined_items"));
    } finally {
      hideLoader();
    }
  };

  const fetchCustomerById = async (customerId) => {
    try {
      const response = await apiManager.post(API_ENDPOINTS.CUSTOMER_GET_BY_ID, {
        id: customerId,
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const fetchVehicleById = async (vehicleId) => {
    try {
      const response = await apiManager.post(
        API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_ID,
        { id: vehicleId },
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const validateInputs = () => {
    let isValid = true;
    const newErrors = { customerName: "", vin: "" };

    if (
      filters.customerName &&
      !/^(?=.*[A-Za-z])[A-Za-z]+(?:[ \-'\.][A-Za-z]+)*$/.test(
        filters.customerName,
      )
    ) {
      newErrors.customerName = localization("customer_name_invalid");
      isValid = false;
    }

    if (filters.vin && !/^[A-Za-z0-9]/.test(filters.vin)) {
      newErrors.vin = localization("vin_invalid_filter");
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const navigateToRepairStep = ({
    row,
    baseState,
    navigate,
    ServiceOrderStatus,
    ServiceAction,
  }) => {
    const { statusCode: status, previousStatusCode: previous, action } = row;

    const goToStep = (stepKey, finalStatus) => {
      navigate("/second-step", {
        state: {
          ...baseState,
          [stepKey]: true,
          status: finalStatus,
        },
      });
    };

    if (status === ServiceOrderStatus.DECLINED) {
      if (
        previous === ServiceOrderStatus.IN_PROGRESS ||
        (previous === ServiceOrderStatus.APPROVAL_EXPIRED &&
          action === ServiceAction.UPDATED) ||
        (previous === ServiceOrderStatus.DECLINED &&
          action === ServiceAction.UPDATED)
      ) {
        goToStep("goToStep5", ServiceOrderStatus.DECLINED);
        return true;
      }

      if (
        previous === ServiceOrderStatus.NEW ||
        (previous === ServiceOrderStatus.APPROVAL_EXPIRED &&
          action === ServiceAction.ADDED) ||
        (previous === ServiceOrderStatus.DECLINED &&
          action === ServiceAction.ADDED)
      ) {
        goToStep("goToStep3", ServiceOrderStatus.DECLINED);
        return true;
      }
    }

    if (status === ServiceOrderStatus.APPROVAL_EXPIRED) {
      if (
        previous === ServiceOrderStatus.NEW ||
        (previous === ServiceOrderStatus.DECLINED &&
          action === ServiceAction.ADDED) ||
        (previous === ServiceOrderStatus.APPROVAL_EXPIRED &&
          action === ServiceAction.ADDED)
      ) {
        goToStep("goToStep3", ServiceOrderStatus.APPROVAL_EXPIRED);
        return true;
      }

      if (
        previous === ServiceOrderStatus.IN_PROGRESS ||
        (previous === ServiceOrderStatus.DECLINED &&
          action === ServiceAction.UPDATED) ||
        (previous === ServiceOrderStatus.APPROVAL_EXPIRED &&
          action === ServiceAction.UPDATED)
      ) {
        goToStep("goToStep5", ServiceOrderStatus.APPROVAL_EXPIRED);
        return true;
      }
    }

    let stepKey = "goToStep3";
    if (status === ServiceOrderStatus.SIGNED) stepKey = "goToStep4";
    else if (status === ServiceOrderStatus.IN_PROGRESS) stepKey = "goToStep5";

    navigate("/second-step", {
      state: {
        ...baseState,
        [stepKey]: true,
        isEditMode: true,
        customer: baseState.customer,
        vehicle: baseState.vehicle,
        customerId: row.customerId,
        vehicleId: row.vehicleId,
        dealershipId: row.dealershipId,
        fromRedirect: true,
      },
    });

    return true;
  };

  const columns = [
    {
      name: localization("service_id"),
      selector: (row) => row.repairOrderId,
      sortable: true,
      width: "100px",
    },
    {
      name: localization("customer"),
      selector: (row) => row.customerName,
      sortable: true,
      wrap: true,
    },
    {
      name: localization("vehicle"),
      selector: (row) =>
        `${row.year || ""} ${row.make || ""} ${row.model || ""}`.trim(),
      sortable: true,
      wrap: true,
    },
    {
      name: localization("vin"),
      selector: (row) => row.vin || localization("not_available"),
      sortable: true,
      wrap: true,
    },
    {
      name: localization("dealership"),
      selector: (row) => row.dealerShipName,
      sortable: true,
      wrap: true,
    },
    {
      name: localization("status"),
      selector: (row) => statusDisplayMap[row.statusCode] || row.statusCode,
      sortable: true,
      width: "120px",
    },
    {
      name: localization("date"),
      selector: (row) =>
        format(new Date(row.createdDate), "MM-dd-yyyy hh:mm a"),
      sortable: true,
      width: "160px",
    },
    {
      name: (
        <div style={{ width: "50%", textAlign: "left" }}>
          {localization("actions")}
        </div>
      ),
      cell: (row) => {
        const isComplete = row.statusCode === ServiceOrderStatus.COMPLETED;
        const tooltipText = isComplete
          ? localization("view")
          : localization("edit");
        const iconClass = isComplete ? "fa fa-eye" : "fa fa-edit";
        return (
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button
              className={`btn btn-sm ${
                isComplete ? "btn-blue" : "btn-golden-yellow"
              }`}
              onClick={async () => {
                try {
                  showLoader();
                  const repairOrderData = await fetchRepairOrderById(
                    row.repairOrderId,
                  );
                  const selectedServices = Array.isArray(repairOrderData)
                    ? repairOrderData.map((service) => ({
                        serviceName: service.serviceName,
                        type: service.serviceCategory,
                        price: service.price,
                        serviceId: service.serviceId,
                        repairOrderId: service.repairOrderId,
                        source: service.source,
                        status: service.status,
                      }))
                    : [];
                  let customerData = null;
                  let vehicleData = null;
                  if (row.customerId) {
                    customerData = await fetchCustomerById(row.customerId);
                  }
                  if (row.vehicleId) {
                    vehicleData = await fetchVehicleById(row.vehicleId);
                  }
                  const baseState = {
                    repairOrder: repairOrderData || row,
                    repairOrderFromInProgress: row.repairOrderId,
                    customerId: row.customerId,
                    vehicleId: row.vehicleId,
                    isApproved: row.isApproved,
                    dealershipId: row.dealershipId,
                    selectedServices,
                    customer: customerData || {
                      name: row.customerName,
                      id: row.customerId,
                      phoneNumber: row.customerPhone,
                      email: row.customerEmail,
                    },
                    status: row.statusCode,
                    vehicle: vehicleData || {
                      year: row.year,
                      make: row.make,
                      model: row.model,
                      vin: row.vin,
                      id: row.vehicleId,
                    },
                  };
                  if (isComplete) {
                    navigate("/printHistory", {
                      state: { repairOrderId: row.repairOrderId },
                    });
                  } else {
                    const handled = navigateToRepairStep({
                      row,
                      baseState,
                      navigate,
                      ServiceOrderStatus,
                      ServiceAction,
                    });
                    if (handled) return;
                  }
                } catch (error) {
                  showErrorMessage(localization("failed_fetch_data_try_again"));
                } finally {
                  hideLoader();
                }
              }}
              title={tooltipText}
            >
              <i className={iconClass} aria-hidden="true"></i>
            </button>
            <button
              className="btn btn-success btn-sm"
              onClick={() => fetchDeclinedItems(row.repairOrderId, row)}
              title={localization("view_declined_items")}
            >
              <i className="fa-solid fa-list" aria-hidden="true"></i>
            </button>
          </div>
        );
      },
      style: { justifyContent: "flex-start" },
      ignoreRowClick: true,
      allowOverflow: true,
      width: "130px",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f4f4f4",
        color: "#333",
        borderRight: "1px solid #ccc",
        fontSize: "16px",
        fontWeight: 600,
        whiteSpace: "nowrap",
      },
    },
    cells: {
      style: {
        borderRight: "1px solid #eee",
        fontSize: "13px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        maxWidth: "300px",
      },
    },
    rows: {
      style: {
        minHeight: "50px",
      },
    },
  };

  useEffect(() => {
    if (routerLocation.state?.statusFilter) {
      setFilters((prev) => ({
        ...prev,
        status: routerLocation.state.statusFilter,
      }));
      setIsStatusLocked(true);
      applyFilters();
      navigate(routerLocation.pathname, { replace: true });
    } else {
      fetchOrders();
    }
  }, []);

  const fetchOrders = async () => {
    try {
      showLoader();
      const payload = {
        dealershipId: selectedDealershipId,
        statusCode: isStatusLocked ? filters.status : null,
        customerName: null,
        vin: null,
        startDate: null,
        endDate: null,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      const response = await apiManager.post(
        API_ENDPOINTS.REPAIR_ORDER_GET_ALL,
        payload,
      );
      const orders = response.data;
      const mappedOrders = orders.map((item, index) => ({
        ...item,
        id: index + 1,
      }));
      setFilteredData(mappedOrders);
    } catch (error) {
      showErrorMessage(error || "Error fetching orders");
    } finally {
      hideLoader();
    }
  };

  const conditionalRowStyles = [
    {
      when: (row) => row.statusCode === ServiceOrderStatus.PENDING_APPROVAL,
      style: {
        backgroundColor: "#d6d8d9",
        pointerEvents: "none",
      },
    },

    {
      when: (row) => row.statusCode === ServiceOrderStatus.DECLINED,
      style: {
        backgroundColor: "#f8d7da",
      },
    },
    {
      when: (row) => row.statusCode === ServiceOrderStatus.APPROVAL_EXPIRED,
      style: {
        backgroundColor: "#fff3cd",
        borderLeft: "3px solid #ffc107",
      },
    },
  ];

  return (
    <div className="container-fluid content-wrapper select-vehicle-content">
      <div className="content form-card">
        <div className="container-fluid p-0">
          <div className="page-title">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-md-6">
                  <h1>{localization("service_orders")}</h1>
                </div>
                <div className="col-md-6"></div>
              </div>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-3 col-lg-2 mb-2 mb-lg-0">
              <input
                type="text"
                className="form-control"
                placeholder={localization("customer_name")}
                value={filters.customerName}
                onChange={(e) =>
                  setFilters({ ...filters, customerName: e.target.value })
                }
              />
              {errors.customerName && (
                <div className="text-danger small">{errors.customerName}</div>
              )}
            </div>
            <div className="col-md-3 col-lg-2 mb-2 mb-lg-0">
              <DatePicker
                selected={filters.fromDate}
                onChange={(date) => setFilters({ ...filters, fromDate: date })}
                className="form-control w-100 d-inline-flex"
                placeholderText={localization("from_date")}
                dateFormat="yyyy-MM-dd"
                maxDate={filters.toDate || new Date()}
              />
            </div>
            <div className="col-md-3 col-lg-2">
              <DatePicker
                selected={filters.toDate}
                onChange={(date) => setFilters({ ...filters, toDate: date })}
                className="form-control"
                placeholderText={localization("to_date")}
                minDate={filters.fromDate}
                maxDate={new Date()}
                dateFormat="yyyy-MM-dd"
              />
            </div>
            <div className="col-md-3 col-lg-2 mb-2 mb-lg-0">
              <div className="dropdown-container">
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                  disabled={isStatusLocked} // <-- disable when locked
                >
                  <option value="">{localization("all_status")}</option>
                  {allowedStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-3 col-lg-2 mb-2 mb-lg-0">
              <input
                type="text"
                className="form-control"
                placeholder={localization("vin")}
                value={filters.vin}
                onChange={(e) =>
                  setFilters({ ...filters, vin: e.target.value })
                }
              />
              {errors.vin && (
                <div className="text-danger small">{errors.vin}</div>
              )}
            </div>
            <div className="col-md-2 d-flex gap-2">
              <button
                className="btn btn-blue d-flex align-items-center justify-content-center gap-2"
                onClick={applyFilters}
                title={localization("apply_filters")}
              >
                <i className="fas fa-filter"></i>
              </button>
              <button
                className="btn btn-danger d-flex align-items-center justify-content-center gap-2"
                onClick={clearFilters}
                title={localization("clear_filters")}
              >
                <i className="fas fa-times"></i>
              </button>
              <button
                className="btn btn-success d-flex align-items-center justify-content-center gap-2"
                onClick={handleDownloadDeclinedServicesReport}
                title={localization("download_declined_report")}
                disabled={filteredData.length === 0}
              >
                <i className="fas fa-download"></i>
              </button>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={filteredData.length > 0 ? filteredData : []}
            pagination={filteredData.length > 0}
            striped
            highlightOnHover
            pointerOnHover
            noDataComponent={localization("no_record_found")}
            responsive
            customStyles={customStyles}
            conditionalRowStyles={conditionalRowStyles}
            noHeader
          />
          <button
            className="btn btn-blue"
            onClick={() => {
              const userRole = getUserRoleFromJWT();
              navigate(
                userRole === UserRole.DEALER_ADMIN
                  ? "/admin-dashboard"
                  : "/service-desk-dashboard",
              );
            }}
          >
            {localization("back")}
          </button>
        </div>
      </div>
      <DeclinedRepairOrdersModal
        isOpen={showDeclinedModal}
        onClose={() => setShowDeclinedModal(false)}
        declinedItems={declinedItems}
        rowData={currentRowData}
      />
    </div>
  );
};

export default ReportOrderListing;
